// 用電 › 報表（月報，plan SPEC NRG-V4；設計稿 v03）：選月份 → 範圍合計 + 依{leaf}摘要表（kWh、比上月、估算電費、碳排與口徑、讀不到）；
// 每個{leaf}點開才載入量測點（前 10 名）；還沒過次月 8 日標「暫定 · 11 月 8 日定案」；〔下載 CSV〕逐{leaf} × 逐日、逐量測點 × 逐月兩份。
// 有缺口不寫百分比，寫「缺 N 小時（M 個量測點）」；PDF 與每月寄送是 E2。
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { energyApi, energyMode } from './api';
import { localParts, type Fmt } from './format';
import { useEnergyEnv, useEnergyTargets, useFmt } from './hooks';
import { addMoney, csvFileName, csvKwh, csvMoney, downloadText, meterName, mergeSummaries, toCsv } from './model';
import { Cmp, LoadError } from './parts';
import type { EnergySummary, HomeRow, MonthReport, RankRow } from './types';

function monthList(n = 13): string[] {
  const now = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
}

/** 次月 8 日（當地）之前是暫定。 */
export function isFinal(month: string, now = new Date()): boolean {
  const [y, m] = month.split('-').map(Number);
  const fin = new Date(m === 12 ? y + 1 : y, m === 12 ? 0 : m, 8);
  return now >= fin;
}

type SortKey = 'mwh' | 'cmp' | 'cost' | 'gaps';

export function Report() {
  const { t } = useTranslation();
  useEnergyEnv();
  const fmt = useFmt();
  const targets = useEnergyTargets();
  const { visible, scopeLabel, homes: allHomes } = useScope();
  const months = useMemo(() => monthList(), []);
  const [month, setMonth] = useState(months[0]);
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [sort, setSort] = useState<SortKey>('mwh');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const qs = useQueries({
    queries: targets.map((tg) => ({
      queryKey: ['energy', 'report', energyMode(), tg.api, tg.org, tg.scope, month],
      queryFn: () => energyApi.report(tg.api, tg.org, tg.scope, month),
      staleTime: 60_000,
      retry: false,
    })),
  });
  const loading = qs.some((q) => q.isLoading);
  const reps = qs.filter((q) => q.data).map((q) => q.data!) as (MonthReport & { names?: EnergySummary['names'] })[];
  const err = qs.find((q) => q.isError)?.error;
  const summary = useMemo(
    () => mergeSummaries(reps.map((r) => ({ ...(r.summary as EnergySummary), series: [], attention: [], names: r.names ?? r.summary.names }))),
    [qs.map((q) => q.dataUpdatedAt).join()], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const rows = reps.flatMap((r) => r.by_home);
  const final = reps.length ? reps.every((r) => r.finalized) : isFinal(month);
  const finalizeOn = reps[0]?.finalize_on;
  const [y, m] = month.split('-').map(Number);
  const homeName = (id: string) => summary?.names?.homes?.[id]?.name ?? allHomes.find((h) => h.home_id === id)?.display_name ?? id;
  const gapRows = rows.filter((r) => r.incomplete_hours > 0 || r.unavailable);
  const sortVal = (r: HomeRow): number =>
    sort === 'cmp' ? (r.comparison?.ratio ?? -Infinity) : sort === 'cost' ? r.costs.reduce((a, c) => a + c.amount, 0) : sort === 'gaps' ? r.incomplete_hours : r.mwh;
  const shown = [...(onlyGaps ? gapRows : rows)].sort((a, b) => sortVal(b) - sortVal(a));

  const download = async (kind: 'homes_daily' | 'meters_monthly') => {
    const prov = final ? undefined : t('energy:report.provisional_tag');
    const name = csvFileName(t('energy:report.csv_prefix'), visible.length === 1 ? (visible[0].display_name ?? '') : scopeLabel, month, prov);
    // 平台有 CSV 端點時用平台的（單一組織）；mock 或多組織時在瀏覽器組
    if (targets.length === 1) {
      const csv = await energyApi.reportCsv(targets[0].api, targets[0].org, targets[0].scope, month, kind);
      if (csv !== null) return downloadText(name, csv);
    }
    const apiOf = (home: string) => allHomes.find((h) => h.home_id === home)?.endpoints?.api ?? targets[0]?.api ?? '';
    const curs = [...new Set(rows.flatMap((r) => r.costs.map((c) => c.currency)))];
    if (kind === 'homes_daily') {
      const last = new Date(y, m, 0).getDate();
      const out: (string | number)[][] = [];
      for (const r of rows) {
        const s = await energyApi.homeSeries(apiOf(r.home_id), r.home_id, 'day', `${month}-01`, `${month}-${String(last).padStart(2, '0')}`);
        for (const p of s.points) {
          if (Date.parse(p.t) > Date.now()) continue;
          const lp = localParts(p.t, summary?.names?.homes?.[r.home_id]?.tz);
          const share = r.mwh ? (p.mwh ?? 0) / r.mwh : 0;
          out.push([
            `${lp.y}-${String(lp.m).padStart(2, '0')}-${String(lp.d).padStart(2, '0')}`,
            homeName(r.home_id),
            csvKwh(p.mwh),
            ...curs.map((c) => {
              const x = r.costs.find((k) => k.currency === c);
              return x ? (x.amount * share).toFixed(2) : '';
            }),
            t(`energy:q.${p.mwh === null ? 'incomplete' : p.quality}`),
          ]);
        }
      }
      return downloadText(
        name,
        toCsv(t('energy:csv.disclaimer'), [t('energy:csv.date'), t('energy:csv.home'), t('energy:csv.kwh'), ...curs.map((c) => t('energy:csv.cost', { cur: c })), t('energy:csv.quality')], out),
      );
    }
    const out: (string | number)[][] = [];
    for (const r of rows) {
      const s = await energyApi.homeSummary(apiOf(r.home_id), r.home_id, 'month', `${month}-01`);
      for (const x of s.meters_detail ?? []) {
        out.push([
          month,
          homeName(r.home_id),
          meterName(t, x, { crossSite: true, homeName: homeName(r.home_id) }).name,
          t(`energy:kind.${x.kind}`),
          csvKwh(x.mwh),
          ...curs.map((c) => csvMoney(x.costs.find((k) => k.currency === c))),
          t(`energy:q.${x.unavailable ? 'incomplete' : x.quality}`),
          x.incomplete_hours,
        ]);
      }
    }
    downloadText(
      name,
      toCsv(
        t('energy:csv.disclaimer'),
        [t('energy:csv.month'), t('energy:csv.home'), t('energy:csv.meter_name'), t('energy:csv.meter_kind'), t('energy:csv.kwh'), ...curs.map((c) => t('energy:csv.cost', { cur: c })), t('energy:csv.quality'), t('energy:csv.missing_h')],
        out,
      ),
    );
  };

  const th = (k: SortKey, label: string) => (
    <th scope="col" className="num" aria-sort={sort === k ? 'descending' : undefined}>
      <button type="button" className="sort" onClick={() => setSort(k)}>
        {label}
        {sort === k && <span aria-hidden> ▼</span>}
      </button>
    </th>
  );

  return (
    <div data-testid="en-report">
      <div className="en-row" style={{ marginBottom: 12 }}>
        <label className="en-row" style={{ gap: 8 }}>
          {t('energy:report.month')}
          <select className="input" value={month} onChange={(e) => (setMonth(e.target.value), setOpen(new Set()))} data-testid="en-report-month">
            {months.map((mo) => {
              const [yy, mm] = mo.split('-').map(Number);
              return (
                <option key={mo} value={mo}>
                  {t('energy:report.month_opt', { y: yy, m: mm })}
                  {isFinal(mo) ? t('energy:report.opt_final') : t('energy:report.opt_provisional')}
                </option>
              );
            })}
          </select>
        </label>
        <span className="spacer" />
        <button type="button" className="btn" onClick={() => void download('homes_daily')} disabled={!rows.length} data-testid="en-csv-homes">
          <span aria-hidden>⤓</span> {t('energy:report.csv_homes')}
        </button>
        <button type="button" className="btn" onClick={() => void download('meters_monthly')} disabled={!rows.length} data-testid="en-csv-meters">
          <span aria-hidden>⤓</span> {t('energy:report.csv_meters')}
        </button>
      </div>
      <h2 className="section-title" data-testid="en-report-title">
        {t('energy:report.month_opt', { y, m })}{' '}
        {final ? (
          <span className="km-st ok">{t('energy:report.final')}</span>
        ) : (
          <span className="km-st attention" data-testid="en-provisional">
            ! {t('energy:report.provisional', { date: finalizeOn ? t('energy:report.date_md', { m: +finalizeOn.slice(5, 7), d: +finalizeOn.slice(8, 10) }) : '' })}
          </span>
        )}
      </h2>
      {err && !reps.length ? (
        <LoadError error={err} retry={() => qs.forEach((q) => void q.refetch())} />
      ) : loading && !reps.length ? (
        <section className="card" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skel" />
          ))}
        </section>
      ) : !summary || !rows.length ? (
        <section className="card">
          <p className="muted">{t('energy:rank.empty')}</p>
        </section>
      ) : (
        <>
          <section className="card en-section" data-testid="en-report-total">
            <h3 className="section-title">{t('energy:report.scope_total')}</h3>
            <dl className="en-kv">
              <dt>{t('energy:csv.kwh')}</dt>
              <dd className="tabular">{fmt.energy(summary.mwh)}</dd>
              <dt>{t('energy:card.cost')}</dt>
              <dd className="tabular">{summary.cost_by_currency.length ? summary.cost_by_currency.map((c) => fmt.money(c)).join(' · ') : t('energy:card.no_tariff_any')}</dd>
              {summary.carbon.length > 0 && summary.show_carbon !== false && (
                <>
                  <dt>{t('energy:card.carbon')}</dt>
                  <dd className="tabular">
                    {summary.carbon.map((c) => `${fmt.carbon(c.kg, c.gas)}（${c.region} · ${t('energy:report.carbon_basis', { gas: c.gas === 'co2e' ? 'CO2e' : 'CO2', year: c.factor_year })}）`).join('；')}
                  </dd>
                </>
              )}
              <dt>{t('energy:table.unavailable')}</dt>
              <dd data-testid="en-report-gaps">
                {summary.incomplete_hours || summary.incomplete_meters ? t('energy:report.gaps', { hours: summary.incomplete_hours, meters: summary.incomplete_meters }) : t('energy:report.no_gaps')}
              </dd>
            </dl>
            <p className="en-note">{t('energy:card.cost_disclaimer')}</p>
          </section>
          <section className="card en-section" data-testid="en-report-homes">
            <div className="en-row">
              <h3 className="section-title" style={{ margin: 0 }}>
                {t('energy:report.summary_by')}
              </h3>
              <span className="spacer" />
              {gapRows.length > 0 && (
                <button type="button" className={`km-chip${onlyGaps ? ' on' : ''}`} aria-pressed={onlyGaps} onClick={() => setOnlyGaps((v) => !v)} data-testid="en-only-gaps">
                  {t('energy:report.only_gaps', { n: gapRows.length })}
                </button>
              )}
            </div>
            <div className="table-wrap">
              <table className="km-table en-sum">
                <thead>
                  <tr>
                    <th scope="col">{t('energy:table.col_home')}</th>
                    {th('mwh', 'kWh')}
                    {th('cmp', t('energy:table.col_cmp'))}
                    {th('cost', t('energy:table.col_cost'))}
                    <th scope="col" className="num">
                      {t('energy:table.col_carbon')}
                    </th>
                    {th('gaps', t('energy:table.col_unavail'))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <HomeReportRows
                      key={r.home_id}
                      r={r}
                      name={homeName(r.home_id)}
                      fmt={fmt}
                      month={month}
                      open={open.has(r.home_id)}
                      toggle={() => setOpen((s) => (s.has(r.home_id) ? new Set([...s].filter((x) => x !== r.home_id)) : new Set([...s, r.home_id])))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="en-note">{t('energy:report.pdf_later')}</p>
          </section>
        </>
      )}
    </div>
  );
}

function HomeReportRows({ r, name, fmt, month, open, toggle }: { r: HomeRow & { top?: RankRow[] }; name: string; fmt: Fmt; month: string; open: boolean; toggle: () => void }) {
  const { t } = useTranslation();
  const { homes } = useScope();
  const api = homes.find((h) => h.home_id === r.home_id)?.endpoints?.api ?? '';
  const [q] = useQueries({
    queries: [{ queryKey: ['energy', 'home-month', energyMode(), r.home_id, month], queryFn: () => energyApi.homeSummary(api, r.home_id, 'month', `${month}-01`), enabled: open, staleTime: 60_000 }],
  });
  const meters = (q.data?.meters_detail ?? r.top ?? []).slice().sort((a, b) => b.mwh - a.mwh);
  const gap = r.incomplete_hours > 0 || r.unavailable;
  const totals = meters.reduce<{ mwh: number; costs: { currency: string; amount: number }[] }>((a, x) => {
    a.mwh += x.mwh;
    for (const c of x.costs) addMoney(a.costs, c);
    return a;
  }, { mwh: 0, costs: [] });
  return (
    <Fragment>
      <tr data-home={r.home_id}>
        <td>
          <button type="button" className="sort" aria-expanded={open} onClick={toggle} aria-label={t('energy:report.expand', { home: name })}>
            <span aria-hidden>{open ? '▾' : '▸'}</span> <b>{name}</b>
          </button>
          {gap && <span className="km-st attention small">{t('energy:report.has_gap')}</span>}
        </td>
        <td className="r tabular">{fmt.energyNum(r.mwh)}</td>
        <td className="r">
          <Cmp fmt={fmt} cmp={r.comparison} cur={r.mwh} reason={r.no_compare_reason} prevCoverage={r.prev_coverage} />
        </td>
        <td className="r tabular">{r.no_tariff ? <span className="muted">{t('energy:table.no_tariff')}</span> : r.costs.map((c) => fmt.money(c)).join(' · ')}</td>
        <td className="r tabular">{(r.carbon ?? []).map((c) => fmt.carbon(c.kg, c.gas)).join(' · ')}</td>
        <td className="r small">{gap ? t('energy:report.gaps', { hours: r.incomplete_hours, meters: r.unavailable_meters }) : ''}</td>
      </tr>
      {open &&
        (q.isLoading ? (
          <tr className="en-port-open">
            <td colSpan={6}>
              <div className="skel" />
            </td>
          </tr>
        ) : (
          <>
            {meters.slice(0, 10).map((x) => {
              const mn = meterName(t, x, { crossSite: true, homeName: name });
              return (
                <tr key={x.meter} className="en-port-open" data-meter={x.meter}>
                  <td style={{ paddingLeft: 32 }}>
                    {mn.name}
                    {mn.sub && <div className="en-note">{mn.sub}</div>}
                  </td>
                  <td className="r tabular">{fmt.energyNum(x.mwh)}</td>
                  <td className="r">
                    <Cmp fmt={fmt} cmp={x.comparison} cur={x.mwh} reason={x.no_compare_reason} />
                  </td>
                  <td className="r tabular">{x.costs.map((c) => fmt.money(c)).join(' · ')}</td>
                  <td />
                  <td className="r small">{x.incomplete_hours ? t('energy:report.gaps', { hours: x.incomplete_hours, meters: 1 }) : ''}</td>
                </tr>
              );
            })}
            <tr className="en-port-open">
              <th scope="row" style={{ paddingLeft: 32, textAlign: 'left' }}>
                {t('energy:chart.total')}
              </th>
              <td className="r tabular">
                <b>{fmt.energyNum(totals.mwh)}</b>
              </td>
              <td />
              <td className="r tabular">
                <b>{totals.costs.map((c) => fmt.money(c)).join(' · ')}</b>
              </td>
              <td colSpan={2} className="en-note">
                {t('energy:report.rounding')}
              </td>
            </tr>
          </>
        ))}
    </Fragment>
  );
}
